import api from './api';
import { cache } from '../utils/cache';

export type ReportMode = 'roasting' | 'greenBeans' | 'roastedBeans' | 'finishedGoods' | 'business' | 'customer';

export interface ReportDateParams {
  startDate?: string;
  endDate?: string;
}

export interface ReportMovement {
  id: string;
  date: string;
  type: string;
  reference: string;
  item_name?: string;
  amount: number;
  cost: number;
  profit: number;
}

export interface GeneralReportResponse {
  movements: ReportMovement[];
  total_revenue: number;
  total_cost: number;
  gross_profit: number;
}

export interface RoastingReportSummary {
  totalBatches: number;
  totalInputWeight: number;
  totalOutputWeight: number;
  totalWeightLoss: number;
  averageLossPercent: number;
}

export interface RoastingReportRow {
  id: string;
  roastingDate: string;
  batchNumber: string;
  greenBeanCode: string;
  greenBeanName: string;
  inputWeight: number;
  outputWeight: number;
  weightLossKg: number;
  weightLossPercent: number;
  roasterName?: string;
  machineName?: string;
}

export interface RoastingReportResponse {
  summary: RoastingReportSummary;
  rows: RoastingReportRow[];
}

export interface GreenBeansReportSummary {
  totalItems: number;
  totalRemainingQuantity: number;
  totalStockValue: number;
}

export interface GreenBeansReportRow {
  itemId: string;
  itemCode: string;
  itemName: string;
  remainingQuantity: number;
  unitOfMeasure: string;
  averageCost: number;
  supplierName: string;
  lastMovementDate?: string;
}

export interface GreenBeansReportResponse {
  summary: GreenBeansReportSummary;
  rows: GreenBeansReportRow[];
}

export interface RoastedBeansReportSummary {
  totalItems: number;
  totalRemainingQuantity: number;
  totalStockValue: number;
}

export interface RoastedBeansReportRow {
  itemId: string;
  itemCode: string;
  itemName: string;
  remainingQuantity: number;
  unitOfMeasure: string;
  averageCost: number;
  relatedBatch: string;
  lastProductionDate?: string;
}

export interface RoastedBeansReportResponse {
  summary: RoastedBeansReportSummary;
  rows: RoastedBeansReportRow[];
}

export interface FinishedGoodsReportSummary {
  totalItems: number;
  totalRemainingQuantity: number;
  totalStockValue: number;
}

export interface FinishedGoodsReportRow {
  itemId: string;
  itemCode: string;
  itemName: string;
  remainingQuantity: number; // pcs
  unitOfMeasure: string;
  averageCost: number;
  lastProductionDate?: string;
}

export interface FinishedGoodsReportResponse {
  summary: FinishedGoodsReportSummary;
  rows: FinishedGoodsReportRow[];
}

export type InventoryItemType = 'green_bean' | 'roasted_bulk' | 'finished_good';

export interface InventoryTrackingEvent {
  date: string;
  direction: 'in' | 'out';
  sourceType: string;
  referenceLabel: string;
  counterparty: string;
  lotNumber?: string;
  quantity: number;
  unit: 'kg' | 'pcs';
}

export interface InventoryTrackingResponse {
  events: InventoryTrackingEvent[];
}

export interface BusinessReportSummary {
  totalRevenue: number;
  totalOrders: number;
  activeCustomers: number;
  averageOrderValue: number;
  netProfit: number;
}

export interface BusinessCustomerRevenueRow {
  customerId?: string;
  customerName: string;
  totalRevenue: number;
  orderCount: number;
  lastOrderDate?: string;
}

export interface BusinessPurchaseHistoryRow {
  orderId: string;
  orderNumber: string;
  orderDate: string;
  customerName: string;
  totalAmount: number;
  status: string;
}

export interface BusinessRevenueTrendPoint {
  label: string;
  periodStart: string;
  revenue: number;
}

export interface BusinessReportResponse {
  summary: BusinessReportSummary;
  customerBreakdown: BusinessCustomerRevenueRow[];
  purchaseHistory: BusinessPurchaseHistoryRow[];
  revenueTrend: BusinessRevenueTrendPoint[];
}

export interface CustomerReportRow {
  customerId: string;
  customerName: string;
  customerCode: string;
  orderCount: number;
  totalPcs: number;
  totalRevenue: number;
  lastOrderDate?: string;
}

export interface CustomerReportResponse {
  customers: CustomerReportRow[];
}

export interface CustomerPurchaseItem {
  itemId: string;
  name: string;
  pcs: number;
}

export interface CustomerFinishedGoodPurchase {
  date: string;
  orderId: string;
  orderNumber: string;
  qty: number;
}

export interface CustomerFinishedGood {
  itemId: string;
  name: string;
  totalPcs: number;
  purchases: CustomerFinishedGoodPurchase[];
}

export interface CustomerPurchaseSummary {
  totalPcs: number;
  totalOrders: number;
  totalRevenue: number;
  topFinishedGood: CustomerPurchaseItem | null;
}

export interface CustomerPurchaseDetailResponse {
  summary: CustomerPurchaseSummary;
  finishedGoods: CustomerFinishedGood[];
}

const emptyRoastingSummary = (): RoastingReportSummary => ({
  totalBatches: 0,
  totalInputWeight: 0,
  totalOutputWeight: 0,
  totalWeightLoss: 0,
  averageLossPercent: 0,
});

const emptyInventorySummary = (): GreenBeansReportSummary => ({
  totalItems: 0,
  totalRemainingQuantity: 0,
  totalStockValue: 0,
});

const emptyBusinessSummary = (): BusinessReportSummary => ({
  totalRevenue: 0,
  totalOrders: 0,
  activeCustomers: 0,
  averageOrderValue: 0,
  netProfit: 0,
});

const buildParams = ({ startDate, endDate }: ReportDateParams) => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  return params;
};

const buildCacheKey = (prefix: string, { startDate, endDate }: ReportDateParams) =>
  `${prefix}_${startDate || 'all'}_${endDate || 'all'}`;

export const reportService = {
  getGeneralReport: async (startDate?: string, endDate?: string) => {
    const key = `reports_general_${startDate || 'all'}_${endDate || 'all'}`;
    return cache.fetch(key, async () => {
      const params = buildParams({ startDate, endDate });
      
      const response = await api.get<GeneralReportResponse>(`/reports/general?${params.toString()}`);
      return response.data;
    });
  },
  getRoastingReport: async (params: ReportDateParams = {}): Promise<RoastingReportResponse> => {
    const key = buildCacheKey('reports_roasting', params);
    return cache.fetch(key, async () => {
      const query = buildParams(params);
      const response = await api.get<Partial<RoastingReportResponse>>(`/reports/roasting?${query.toString()}`);
      return {
        summary: response.data.summary ? { ...emptyRoastingSummary(), ...response.data.summary } : emptyRoastingSummary(),
        rows: Array.isArray(response.data.rows) ? response.data.rows : [],
      };
    });
  },
  getGreenBeansReport: async (params: ReportDateParams = {}): Promise<GreenBeansReportResponse> => {
    const key = buildCacheKey('reports_green_beans', params);
    return cache.fetch(key, async () => {
      const query = buildParams(params);
      const response = await api.get<Partial<GreenBeansReportResponse>>(`/reports/green-beans?${query.toString()}`);
      return {
        summary: response.data.summary ? { ...emptyInventorySummary(), ...response.data.summary } : emptyInventorySummary(),
        rows: Array.isArray(response.data.rows) ? response.data.rows : [],
      };
    });
  },
  getRoastedBeansReport: async (params: ReportDateParams = {}): Promise<RoastedBeansReportResponse> => {
    const key = buildCacheKey('reports_roasted_beans', params);
    return cache.fetch(key, async () => {
      const query = buildParams(params);
      const response = await api.get<Partial<RoastedBeansReportResponse>>(`/reports/roasted-beans?${query.toString()}`);
      return {
        summary: response.data.summary ? { ...emptyInventorySummary(), ...response.data.summary } : emptyInventorySummary(),
        rows: Array.isArray(response.data.rows) ? response.data.rows : [],
      };
    });
  },
  getFinishedGoodsReport: async (params: ReportDateParams = {}): Promise<FinishedGoodsReportResponse> => {
    const key = buildCacheKey('reports_finished_goods', params);
    return cache.fetch(key, async () => {
      const query = buildParams(params);
      const response = await api.get<Partial<FinishedGoodsReportResponse>>(`/reports/finished-goods?${query.toString()}`);
      return {
        summary: response.data.summary ? { ...emptyInventorySummary(), ...response.data.summary } : emptyInventorySummary(),
        rows: Array.isArray(response.data.rows) ? response.data.rows : [],
      };
    });
  },
  getInventoryTracking: async (
    itemId: string,
    itemType: InventoryItemType
  ): Promise<InventoryTrackingResponse> => {
    const params = new URLSearchParams({ item_id: itemId, item_type: itemType });
    const response = await api.get<Partial<InventoryTrackingResponse>>(
      `/reports/inventory/tracking?${params.toString()}`
    );
    return { events: Array.isArray(response.data.events) ? response.data.events : [] };
  },
  getBusinessReport: async (params: ReportDateParams = {}): Promise<BusinessReportResponse> => {
    const key = buildCacheKey('reports_business', params);
    return cache.fetch(key, async () => {
      const query = buildParams(params);
      const response = await api.get<Partial<BusinessReportResponse>>(`/reports/business?${query.toString()}`);
      return {
        summary: response.data.summary ? { ...emptyBusinessSummary(), ...response.data.summary } : emptyBusinessSummary(),
        customerBreakdown: Array.isArray(response.data.customerBreakdown) ? response.data.customerBreakdown : [],
        purchaseHistory: Array.isArray(response.data.purchaseHistory) ? response.data.purchaseHistory : [],
        revenueTrend: Array.isArray(response.data.revenueTrend) ? response.data.revenueTrend : [],
      };
    });
  },
  getCustomerReport: async (params: ReportDateParams = {}): Promise<CustomerReportResponse> => {
    const key = buildCacheKey('reports_customers', params);
    return cache.fetch(key, async () => {
      const query = buildParams(params);
      const response = await api.get<Partial<CustomerReportResponse>>(`/reports/customers?${query.toString()}`);
      return {
        customers: Array.isArray(response.data.customers) ? response.data.customers : [],
      };
    });
  },
  getCustomerPurchaseDetail: async (
    customerId: string,
    params: ReportDateParams = {}
  ): Promise<CustomerPurchaseDetailResponse> => {
    const key = buildCacheKey(`reports_customer_detail_${customerId}`, params);
    return cache.fetch(key, async () => {
      const query = buildParams(params);
      const response = await api.get<Partial<CustomerPurchaseDetailResponse>>(
        `/reports/customers/${customerId}/purchases?${query.toString()}`
      );
      return {
        summary: response.data.summary ?? { totalPcs: 0, totalOrders: 0, totalRevenue: 0, topFinishedGood: null },
        finishedGoods: Array.isArray(response.data.finishedGoods) ? response.data.finishedGoods : [],
      };
    });
  },
};
